import React, { useState, useEffect } from 'react';
import { Problem, Project } from '../types';
import AutocompleteInput from './AutocompleteInput';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ProblemFormProps {
  problem?: Problem;
  projects: Project[];
  onSubmit: (problem: Problem) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const ProblemForm: React.FC<ProblemFormProps> = ({
  problem,
  projects,
  onSubmit,
  onCancel,
  isEditing = false
}) => {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const { get } = useApi();

  const [formData, setFormData] = useState<Omit<Problem, 'id' | 'created_at'>>({
    title: '',
    description: '',
    impact_area: '',
    priority: 5,
    project_id: '',
    stakeholders: [],
    metrics: []
  });

  const [stakeholderName, setStakeholderName] = useState('');
  const [stakeholderRole, setStakeholderRole] = useState('');
  const [metricName, setMetricName] = useState('');
  const [metricDescription, setMetricDescription] = useState('');
  const [metricCurrentValue, setMetricCurrentValue] = useState('');
  const [metricTargetValue, setMetricTargetValue] = useState('');
  const [metricUnit, setMetricUnit] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Autocomplete suggestions
  const [stakeholderNameSuggestions, setStakeholderNameSuggestions] = useState<string[]>([]);
  const [stakeholderRoleSuggestions, setStakeholderRoleSuggestions] = useState<string[]>([]);
  const [metricNameSuggestions, setMetricNameSuggestions] = useState<string[]>([]);
  const [metricUnitSuggestions, setMetricUnitSuggestions] = useState<string[]>([]);

  // Redirect viewers away from form
  useEffect(() => {
    if (!canWrite()) {
      navigate('/app-problems');
    }
  }, [canWrite, navigate]);

  useEffect(() => {
    if (problem) {
      setFormData({
        title: problem.title,
        description: problem.description,
        impact_area: problem.impact_area,
        priority: problem.priority,
        project_id: problem.project_id || '',
        stakeholders: problem.stakeholders || [],
        metrics: problem.metrics || []
      });
    }
  }, [problem]);

  // Fetch existing data for autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const problems = await get<Problem[]>('/problems');
        
        // Extract unique stakeholder names, roles, metric names, and units
        const stakeholderNames = new Set<string>();
        const stakeholderRoles = new Set<string>();
        const metricNames = new Set<string>();
        const metricUnits = new Set<string>();
        
        // Common default units
        const commonUnits = ['%', '$', '€', '£', 'hrs', 'min', 'sec', 'days', 'weeks', 'months', 'count', 'users', 'tickets', 'points', 'score', 'MB', 'GB', 'TB', 'requests', 'clicks', 'views', 'sessions'];
        commonUnits.forEach(unit => metricUnits.add(unit));
        
        problems.forEach(problem => {
          problem.stakeholders?.forEach(stakeholder => {
            stakeholderNames.add(stakeholder.name);
            stakeholderRoles.add(stakeholder.role);
          });
          problem.metrics?.forEach(metric => {
            metricNames.add(metric.name);
            if (metric.unit && metric.unit.trim()) {
              metricUnits.add(metric.unit);
            }
          });
        });
        
        setStakeholderNameSuggestions(Array.from(stakeholderNames));
        setStakeholderRoleSuggestions(Array.from(stakeholderRoles));
        setMetricNameSuggestions(Array.from(metricNames));
        setMetricUnitSuggestions(Array.from(metricUnits).sort());
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);
      }
    };
    
    fetchSuggestions();
  }, [get]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check for unsaved stakeholder data
    if (stakeholderName.trim() || stakeholderRole.trim()) {
      alert('You have unsaved stakeholder information. Please click "Add Stakeholder" or clear the fields before submitting.');
      return;
    }
    
    // Check for unsaved metric data
    if (metricName.trim() || metricDescription.trim() || metricCurrentValue.trim() || metricTargetValue.trim() || metricUnit.trim()) {
      alert('You have unsaved metric information. Please click "Add Metric" or clear the fields before submitting.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const problemData = {
        ...formData,
        id: problem?.id || '',
        created_at: problem?.created_at || new Date().toISOString(),
        stakeholders: formData.stakeholders || [],
        metrics: formData.metrics || []
      } as Problem;

      onSubmit(problemData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addStakeholder = () => {
    if (stakeholderName.trim() && stakeholderRole.trim()) {
      const newStakeholders = [
        ...(formData.stakeholders || []),
        { name: stakeholderName.trim(), role: stakeholderRole.trim() }
      ];
      setFormData({
        ...formData,
        stakeholders: newStakeholders
      });
      setStakeholderName('');
      setStakeholderRole('');
    }
  };

  const removeStakeholder = (index: number) => {
    setFormData({
      ...formData,
      stakeholders: formData.stakeholders?.filter((_, i) => i !== index) || []
    });
  };

  const addMetric = () => {
    if (metricName.trim() && metricDescription.trim()) {
      const metric: {
        name: string;
        description: string;
        current_value?: number;
        target_value?: number;
        unit?: string;
      } = {
        name: metricName.trim(),
        description: metricDescription.trim(),
        ...(metricCurrentValue && { current_value: parseFloat(metricCurrentValue) }),
        ...(metricTargetValue && { target_value: parseFloat(metricTargetValue) }),
        ...(metricUnit.trim() && { unit: metricUnit.trim() })
      };

      setFormData({
        ...formData,
        metrics: [...(formData.metrics || []), metric]
      });
      
      setMetricName('');
      setMetricDescription('');
      setMetricCurrentValue('');
      setMetricTargetValue('');
      setMetricUnit('');
    }
  };

  const removeMetric = (index: number) => {
    setFormData({
      ...formData,
      metrics: formData.metrics?.filter((_, i) => i !== index) || []
    });
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6">
        {isEditing ? 'Edit Problem' : 'Create New Problem'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description *
          </label>
          <textarea
            id="description"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div>
          <label htmlFor="impact_area" className="block text-sm font-medium text-gray-700">
            Impact Area *
          </label>
          <input
            type="text"
            id="impact_area"
            value={formData.impact_area}
            onChange={(e) => setFormData({ ...formData, impact_area: e.target.value })}
            placeholder="e.g. Operations, Customer Service, Sales"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
            Priority ({formData.priority}/10)
          </label>
          <input
            type="range"
            id="priority"
            min="1"
            max="10"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
            className="mt-1 block w-full"
          />
          <div className="text-xs text-gray-500 mt-1">Higher = more urgent/important</div>
        </div>

        <div>
          <label htmlFor="project_id" className="block text-sm font-medium text-gray-700">
            Project
          </label>
          <select
            id="project_id"
            value={formData.project_id}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            required
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">Select a project *</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        {/* Stakeholders Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Stakeholders
          </label>
          
          {/* List of added stakeholders */}
          {formData.stakeholders && formData.stakeholders.length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-gray-600">Added Stakeholders:</h4>
              {formData.stakeholders.map((stakeholder, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                  <span className="text-sm">
                    <strong>{stakeholder.name}</strong> - {stakeholder.role}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeStakeholder(index)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Add new stakeholder form */}
          <div className={`border-t pt-3 ${(stakeholderName.trim() || stakeholderRole.trim()) ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}>
            <h4 className="text-sm font-medium text-gray-600 mb-2">
              Add New Stakeholder:
              {(stakeholderName.trim() || stakeholderRole.trim()) && (
                <span className="text-orange-600 text-xs ml-2">⚠ Unsaved data - click "Add Stakeholder" to save</span>
              )}
            </h4>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <AutocompleteInput
                value={stakeholderName}
                onChange={setStakeholderName}
                suggestions={stakeholderNameSuggestions}
                placeholder="Enter stakeholder name"
                className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <AutocompleteInput
                value={stakeholderRole}
                onChange={setStakeholderRole}
                suggestions={stakeholderRoleSuggestions}
                placeholder="Enter role"
                className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <button
              type="button"
              onClick={addStakeholder}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              disabled={!stakeholderName.trim() || !stakeholderRole.trim()}
            >
              + Add Stakeholder
            </button>
          </div>
        </div>

        {/* Metrics Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Metrics
          </label>
          
          {/* List of added metrics */}
          {formData.metrics && formData.metrics.length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-gray-600">Added Metrics:</h4>
              {formData.metrics.map((metric, index) => (
                <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{metric.name}</div>
                      <div className="text-xs text-gray-600 mt-1">{metric.description}</div>
                      {(metric.current_value !== undefined || metric.target_value !== undefined) && (
                        <div className="text-xs text-gray-500 mt-1">
                          {metric.current_value !== undefined && `Current: ${metric.current_value}${metric.unit || ''}`}
                          {metric.current_value !== undefined && metric.target_value !== undefined && ' → '}
                          {metric.target_value !== undefined && `Target: ${metric.target_value}${metric.unit || ''}`}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMetric(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium ml-2"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Add new metric form */}
          <div className={`border-t pt-3 ${(metricName.trim() || metricDescription.trim() || metricCurrentValue.trim() || metricTargetValue.trim() || metricUnit.trim()) ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}>
            <h4 className="text-sm font-medium text-gray-600 mb-2">
              Add New Metric:
              {(metricName.trim() || metricDescription.trim() || metricCurrentValue.trim() || metricTargetValue.trim() || metricUnit.trim()) && (
                <span className="text-orange-600 text-xs ml-2">⚠ Unsaved data - click "Add Metric" to save</span>
              )}
            </h4>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <AutocompleteInput
                  value={metricName}
                  onChange={setMetricName}
                  suggestions={metricNameSuggestions}
                  placeholder="Enter metric name"
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <input
                  type="text"
                  placeholder="Enter description"
                  value={metricDescription}
                  onChange={(e) => setMetricDescription(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  step="any"
                  placeholder="Current value (optional)"
                  value={metricCurrentValue}
                  onChange={(e) => setMetricCurrentValue(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <input
                  type="number"
                  step="any"
                  placeholder="Target value (optional)"
                  value={metricTargetValue}
                  onChange={(e) => setMetricTargetValue(e.target.value)}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <AutocompleteInput
                  value={metricUnit}
                  onChange={setMetricUnit}
                  suggestions={metricUnitSuggestions}
                  placeholder="Unit (e.g. %, $, hrs)"
                  className="border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <button
                type="button"
                onClick={addMetric}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                disabled={!metricName.trim() || !metricDescription.trim()}
              >
                + Add Metric
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Problem' : 'Create Problem')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProblemForm;